<template>
  <div>
    <div v-if="showMeterEquipmentStats">
      <SectionTitle>Meter Equipment</SectionTitle>
      <PieChart :data="meterDeviceData" empty-message="No Meter Equipment" />
      <div class="text-center my-4">
        <BaseClearButton size="small" @click="goToUnits">
          <i class="icon-eye mr-2 text-base"></i>
          View Units
        </BaseClearButton>
      </div>
    </div>
    <div v-if="showLeakSensorStats">
      <SectionTitle>Leak Sensors</SectionTitle>
      <PieChart :data="leakSensorData" empty-message="No Leak Sensors" />
      <div class="text-center my-4">
        <BaseClearButton size="small" @click="goToUnits">
          <i class="icon-eye mr-2 text-base"></i>
          View Units
        </BaseClearButton>
      </div>
    </div>
    <div v-if="showMRAStats">
      <SectionTitle>Manual Read Meters</SectionTitle>
      <PieChart :data="manualReadMeterData" empty-message="No Manual Read Equipment" />
      <div class="text-center my-4">
        <BaseClearButton size="small" @click="goToUnits">
          <i class="icon-eye mr-2 text-base"></i>
          View Units
        </BaseClearButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import PieChart, { Datum } from '@/components/charts/PieChart.vue';
import BaseClearButton from '@/components/forms/BaseClearButton.vue';
import SectionTitle from '@/components/forms/SectionTitle.vue';
import { UnitService } from '@/http-services/unit.service';
import { useSelectedProperty } from '@/stores/selected-property.store';
import { createAnimation, useIonRouter } from '@ionic/vue';
import { SubscriptionTypeIds } from '@ncss/models/dist/v3';
import { PropertyEquipmentStats } from 'common';
import { computed, shallowRef, watch } from 'vue';

const selectedProperty = useSelectedProperty();
const unitService = new UnitService();
const meterDeviceData = shallowRef<Datum[]>([]);
const leakSensorData = shallowRef<Datum[]>([]);
const manualReadMeterData = shallowRef<Datum[]>([]);
const stats = shallowRef<PropertyEquipmentStats>();
const router = useIonRouter();

const showMeterEquipmentStats = computed(() => {
  const p = selectedProperty.property;
  const data = meterDeviceData.value;
  if (data.length) {
    return true;
  }
  if (p?.isSubscriptionEnabled(SubscriptionTypeIds.MANUAL_READS)) {
    return false;
  }
  return true;
});

const showLeakSensorStats = computed(() => {
  const p = selectedProperty.property;
  const data = leakSensorData.value;
  if (data.length) {
    return true;
  }
  if (p?.isSubscriptionEnabled(SubscriptionTypeIds.LEAK_MONITORING)) {
    return true;
  }
  return false;
});

const showMRAStats = computed(() => {
  const p = selectedProperty.property;
  const data = manualReadMeterData.value;
  if (data.length) {
    return true;
  }
  if (p?.isSubscriptionEnabled(SubscriptionTypeIds.MANUAL_READS)) {
    return true;
  }
  return false;
});

if (selectedProperty.propertyId) {
  loadStats(selectedProperty.propertyId);
}
watch(
  () => selectedProperty.propertyId,
  (id, oldId) => {
    if (id && oldId !== id) {
      loadStats(id);
    }
  }
);

function loadStats(propertyId: string) {
  unitService.equipmentStats(propertyId).then((res) => {
    stats.value = res;
    if (res.meterDevices.count === 0) {
      meterDeviceData.value = [];
    } else {
      meterDeviceData.value = [
        { name: 'Online', value: res.meterDevices.online.count, color: 'blue' },
        { name: 'Offline', value: res.meterDevices.offline.count, color: 'red' },
        { name: 'Not Installed', value: res.meterDevices.notInstalled, color: 'gray' },
      ];
    }
    if (res.leakSensors.count === 0) {
      leakSensorData.value = [];
    } else {
      leakSensorData.value = [
        { name: 'Online', value: res.leakSensors.online.count, color: 'blue' },
        { name: 'Offline', value: res.leakSensors.offline.count, color: 'red' },
        { name: 'Not Installed', value: res.leakSensors.notInstalled, color: 'gray' },
      ];
    }
    if (res.manualReadMeters.count === 0) {
      manualReadMeterData.value = [];
    } else {
      manualReadMeterData.value = [
        { name: 'No Read', value: res.manualReadMeters.invalidRead, color: 'gray' },
        { name: 'Valid Read', value: res.manualReadMeters.validRead, color: 'blue' },
      ];
    }
  });
}

function goToUnits() {
  return router.push(`/units/${selectedProperty.propertyId}`);
}
</script>
